import { type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-muted text-foreground",
        outline: "border-border text-foreground",
        success: "border-transparent bg-emerald-100 text-emerald-700",
        warning: "border-transparent bg-amber-100 text-amber-700",
        danger: "border-transparent bg-rose-100 text-rose-700",
        info: "border-transparent bg-sky-100 text-sky-700",
        // v2.1.0 — separate from "danger" so rejected (recoverable) and
        // blacklisted (permanent) read as distinct severity tiers.
        dark: "border-transparent bg-slate-700 text-slate-50",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// ---------------------------------------------------------------------------
// v2.1.0 — lifecycle visual mapping rewrite.
//
// Earlier (v2.0.x) "candidate" and "active" both rendered as success/green —
// they're distinct funnel stages (passed-eval-not-yet-engaged vs currently-
// engaged) so an operator scanning a list couldn't tell them apart at a
// glance. Same problem at rejected vs blacklisted (both red).
//
// New mapping follows the channel funnel:
//
//   untracked / discovered → outline / info       (system-noticed, undecided)
//   evaluating             → warning              (action needed, in queue)
//   candidate              → info                 (passed eval, ready)
//   active                 → success              (currently engaged — green
//                                                  reserved for this terminal
//                                                  positive state)
//   monitor_only           → secondary            (passive side-branch)
//   archived               → secondary            (concluded inactive)
//   rejected               → danger               (declined, recoverable)
//   blacklisted            → dark                 (permanent block)
// ---------------------------------------------------------------------------
export function lifecycleVariant(status: string): BadgeProps["variant"] {
  switch (status) {
    case "active":
      return "success";
    case "candidate":
      return "info";
    case "evaluating":
      return "warning";
    case "discovered":
      return "info";
    case "monitor_only":
      return "secondary";
    case "untracked":
      return "outline";
    case "archived":
      return "secondary";
    case "rejected":
      return "danger";
    case "blacklisted":
      return "dark";
    default:
      return "secondary";
  }
}

// Chinese labels for lifecycle. Keep DB strings as the source of truth (i.e.
// API still returns "evaluating"); this is presentation only. Unknown values
// fall through to the raw string so we don't silently hide unmapped states.
export function lifecycleLabel(status: string): string {
  switch (status) {
    case "untracked":
      return "未追踪";
    case "discovered":
      return "已发现";
    case "evaluating":
      return "评估中";
    case "candidate":
      return "候选";
    case "active":
      return "活跃";
    case "monitor_only":
      return "仅观察";
    case "archived":
      return "已归档";
    case "rejected":
      return "已拒绝";
    case "blacklisted":
      return "黑名单";
    default:
      return status;
  }
}

export function taskStatusVariant(status: string): BadgeProps["variant"] {
  switch (status) {
    case "scored":
      return "info";
    case "decided":
      return "success";
    case "fetching":
      return "info";
    case "pending":
      return "secondary";
    case "failed":
      return "danger";
    default:
      return "outline";
  }
}

export function taskStatusLabel(status: string): string {
  switch (status) {
    case "pending":
      return "排队中";
    case "fetching":
      return "抓取中";
    case "scored":
      return "已评分";
    case "decided":
      return "已决策";
    case "failed":
      return "失败";
    default:
      return status;
  }
}

// decisionVariant distinguishes manual vs auto outcomes. Manual decisions
// use solid color; auto decisions use the lighter info/warning tint so the
// "who decided this" answer is visible at a glance.
export function decisionVariant(decision: string): BadgeProps["variant"] {
  switch (decision) {
    case "candidate":
      return "success";
    case "auto_accept":
      return "info";
    case "rejected":
      return "danger";
    case "auto_reject":
      return "warning";
    case "monitor_only":
      return "secondary";
    case "deferred":
      return "outline";
    case "manual_review":
      return "info";
    // Sponsorship review status (re-uses the decisionVariant helper).
    case "confirmed":
      return "success";
    case "pending":
      return "warning";
    default:
      return "secondary";
  }
}

export function decisionLabel(decision: string): string {
  switch (decision) {
    case "candidate":
      return "候选";
    case "auto_accept":
      return "自动通过";
    case "rejected":
      return "拒绝";
    case "auto_reject":
      return "自动拒绝";
    case "monitor_only":
      return "仅观察";
    case "deferred":
      return "暂缓";
    case "manual_review":
      return "人工复核";
    case "confirmed":
      return "已确认";
    case "pending":
      return "待审";
    default:
      return decision;
  }
}

export function priorityVariant(priority: string): BadgeProps["variant"] {
  switch (priority) {
    case "high":
      return "danger";
    case "medium":
      return "info";
    case "low":
      return "secondary";
    default:
      return "outline";
  }
}

// v2.1.0 — confidence (high/medium/low) is shown raw English across the UI;
// give callers a Chinese label + a single variant helper so review pages
// stop hand-rolling their own map.
export function confidenceVariant(c: string): BadgeProps["variant"] {
  switch (c) {
    case "high":
      return "success";
    case "medium":
      return "warning";
    case "low":
      return "secondary";
    default:
      return "outline";
  }
}

export function confidenceLabel(c: string): string {
  switch (c) {
    case "high":
      return "高";
    case "medium":
      return "中";
    case "low":
      return "低";
    default:
      return c;
  }
}
